import { APP_ROLE_PARAM_KEY, Role } from 'auth';
import { AddUserResponse, UserRole } from 'compliance-api/generated';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { enumToOptions } from '@/modules/common/utils/enum-to-select-options.ts';
import DepartmentsSelectPaginated from '@/modules/admin-dashboard/components/DepartmentsSelectPaginated.tsx';
import { useAddUsersToDepartmentMutation } from '@/modules/admin-dashboard/queries/department.ts';
import { useAddUserMutation } from '@/modules/admin-dashboard/queries/users.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  Select,
  SelectPaginateOption,
  TextInputField,
} from 'ui';

const roleOptions = enumToOptions(UserRole);

const initialValues = {
  departments: [] as SelectPaginateOption<number>[],
  first_name: '',
  last_name: '',
  email: '',
  role: UserRole.User,
};

interface FormValues {
  departments: number[];
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole;
}

const normalizeName = (value: string) => value.trim().replace(/\s+/g, ' ');

const validationSchema = Yup.object().shape({
  first_name: Yup.string()
    .transform(value => (typeof value === 'string' ? normalizeName(value) : value))
    .min(3, 'First name must be at least 3 characters')
    .max(255, 'First name must be at most 255 characters')
    .required('First name is required'),
  last_name: Yup.string()
    .transform(value => (typeof value === 'string' ? normalizeName(value) : value))
    .min(3, 'Last name must be at least 3 characters')
    .max(255, 'Last name must be at most 255 characters')
    .required('Last name is required'),
  email: Yup.string().email().required('Email is required'),
  role: Yup.string().required('Role is required'),
});

interface AddUserModalProps extends BaseModalProps {
  onSuccess?: (response: AddUserResponse) => void;
}

export const AddUserModal = ({ open, onSuccess, onClose }: AddUserModalProps) => {
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        await handleSubmit({
          ...values,
          departments: values.departments.map(value => value.value),
        });
        handleCloseModal();
      } catch {
        // The mutation has already surfaced the API error through withErrorHandling.
      }
    },
  });

  const handleSelectDepartments = (options: SelectPaginateOption<number>[]) => {
    formik.setFieldValue('departments', options);
  };

  const handleSelectRole = (option: string | null) => {
    formik.setFieldValue('role', option ? option : undefined);
  };

  const { mutateAsync, isLoading: createLoading } = useAddUserMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: addUserMutation } = useAddUsersToDepartmentMutation({
    setErrors: formik.setErrors,
  });

  const handleSubmit = async (values: FormValues) => {
    const roleParam =
      (values.role === UserRole.User && Role.COMPLIANCE_USER) ||
      (values.role === UserRole.Admin && Role.COMPLIANCE_ADMIN) ||
      '';
    const res = await mutateAsync({
      first_name: normalizeName(values.first_name),
      last_name: normalizeName(values.last_name),
      email: values?.email,
      role: values?.role,
      redirect_url: `${
        import.meta.env.VITE_KEYCLOAK_REDIRECT_URI
      }?${APP_ROLE_PARAM_KEY}=${roleParam}`,
    });

    onSuccess && onSuccess(res);

    if (values.departments.length && res.user) {
      await addUserMutation({ department_ids: values.departments, user_ids: [res.user.id] });
    }
  };

  const handleCloseModal = () => {
    formik.resetForm();
    onClose();
  };
  return (
    <ModalContentWrapper open={open} title="Add new user" onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={e => e.preventDefault()}>
          <div className="md:flex md:gap-10">
            <TextInputField
              label="First name"
              name="first_name"
              placeholder="Enter user first name"
            />
            <TextInputField label="Last Name" name="last_name" placeholder="Enter user last name" />
          </div>
          <div className="w-full flex flex-col space-y-8">
            <TextInputField label="Email" name="email" placeholder="Enter user email" />
            <Select
              value={formik.values.role}
              onChange={handleSelectRole}
              name="role"
              autoHeight={false}
              options={roleOptions}
              label="Role"
            />
          </div>
          <DepartmentsSelectPaginated
            enableCreation
            value={formik.values.departments}
            isMulti={true}
            label="Departments"
            formFieldTitle="departments"
            handleSelectDepartments={handleSelectDepartments}
          />
          <Button loading={createLoading} onClick={formik.submitForm}>
            Send invitation
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
